import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocalizedLink from './LocalizedLink';
import { Type, Copy, Check } from 'lucide-react';
import SEOHead from './SEOHead';

const CaseConverterIndex = () => {
  const { t } = useTranslation();
  const [inputText, setInputText] = useState('');
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Character and word count
  const charCount = inputText.length;
  const wordCount = inputText.trim() === '' ? 0 : inputText.trim().split(/\s+/).length;

  // Conversion functions
  const toUpperCase = (text) => text.toUpperCase();

  const toLowerCase = (text) => text.toLowerCase();

  const toTitleCase = (text) => {
    return text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const toSentenceCase = (text) => {
    return text.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, (char) => char.toUpperCase());
  };

  const toCamelCase = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase());
  };

  const toPascalCase = (text) => {
    const camel = toCamelCase(text);
    return camel.charAt(0).toUpperCase() + camel.slice(1);
  };

  const toSnakeCase = (text) => {
    return text
      .trim()
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .replace(/[\s-]+/g, '_')
      .toLowerCase();
  };

  const toKebabCase = (text) => {
    return text
      .trim()
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase();
  };

  const toConstantCase = (text) => {
    return toSnakeCase(text).toUpperCase();
  };

  const toAlternatingCase = (text) => {
    return text
      .split('')
      .map((char, index) => (index % 2 === 0 ? char.toLowerCase() : char.toUpperCase()))
      .join('');
  };

  const reverseText = (text) => {
    return text.split('').reverse().join('');
  };

  // Conversion options
  const conversions = [
    { id: 'uppercase', label: t('converters.case.uppercase'), func: toUpperCase },
    { id: 'lowercase', label: t('converters.case.lowercase'), func: toLowerCase },
    { id: 'titlecase', label: t('converters.case.titleCase'), func: toTitleCase },
    { id: 'sentencecase', label: t('converters.case.sentenceCase'), func: toSentenceCase },
    { id: 'camelcase', label: t('converters.case.camelCase'), func: toCamelCase },
    { id: 'pascalcase', label: t('converters.case.pascalCase'), func: toPascalCase },
    { id: 'snakecase', label: t('converters.case.snakeCase'), func: toSnakeCase },
    { id: 'kebabcase', label: t('converters.case.kebabCase'), func: toKebabCase },
    { id: 'constantcase', label: t('converters.case.constantCase'), func: toConstantCase },
    { id: 'alternating', label: t('converters.case.alternatingCase'), func: toAlternatingCase },
    { id: 'reverse', label: t('converters.case.reverseText'), func: reverseText },
  ];

  // Copy to clipboard function
  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div className="space-y-6">
      <SEOHead
        title={t('converters.case.name')}
        description={t('converters.case.seoDescription')}
      />

      {/* Header with Description */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <div className="flex items-center gap-2">
            <Type className="h-5 w-5 text-white" />
            <h1 className="text-lg font-semibold text-white">{t('converters.case.title', t('converters.case.name'))}</h1>
          </div>
        </div>
        <div className="p-4">
          <p className="text-gray-600 text-sm">
            {t('converters.case.pageDescription')}
          </p>
        </div>
      </div>

      {/* Main Converter Tool */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="space-y-4">
            {/* Text Input Area */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('converters.case.enterYourText')}
              </label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent text-base resize-y"
                placeholder={t('converters.case.placeholder')}
                rows="6"
              />
            </div>

            {/* Character and Word Count */}
            <div className="flex gap-6 text-sm text-gray-600">
              <div>
                <span className="font-medium">{t('converters.case.characters')}</span> {charCount}
              </div>
              <div>
                <span className="font-medium">{t('converters.case.words')}</span> {wordCount}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conversion Results */}
      {inputText && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-800">{t('converters.case.convertedResults')}</h2>
          </div>
          <div className="p-4">
            <div className="space-y-4">
              {conversions.map((conversion, index) => {
                const result = conversion.func(inputText);
                return (
                  <div
                    key={conversion.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-[#4a7c9b] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">
                          {conversion.label}
                        </h3>
                        <p className="text-gray-900 break-words whitespace-pre-wrap">
                          {result}
                        </p>
                      </div>
                      <button
                        onClick={() => copyToClipboard(result, index)}
                        className="flex-shrink-0 p-2 rounded-lg bg-[#4a7c9b] text-white hover:bg-[#3d6a86] transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedIndex === index ? (
                          <Check size={18} />
                        ) : (
                          <Copy size={18} />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Information Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('converters.case.aboutCaseConverter')}</h2>
        </div>
        <div className="p-4">
          <div className="space-y-4 text-sm text-gray-600">
            <div>
              <h3 className="font-medium text-gray-800 mb-1">{t('converters.case.whatIsCaseConverter')}</h3>
              <p>
                {t('converters.case.whatIsCaseConverterDescription')}
              </p>
            </div>

            <div>
              <h3 className="font-medium text-gray-800 mb-1">{t('converters.case.commonUseCases')}</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>{t('converters.case.camelCase')} & {t('converters.case.pascalCase')}:</strong> {t('converters.case.camelPascalUseCase')}</li>
                <li><strong>{t('converters.case.snakeCase')}:</strong> {t('converters.case.snakeCaseUseCase')}</li>
                <li><strong>{t('converters.case.kebabCase')}:</strong> {t('converters.case.kebabCaseUseCase')}</li>
                <li><strong>{t('converters.case.constantCase')}:</strong> {t('converters.case.constantCaseUseCase')}</li>
                <li><strong>{t('converters.case.titleCase')}:</strong> {t('converters.case.titleCaseUseCase')}</li>
                <li><strong>{t('converters.case.sentenceCase')}:</strong> {t('converters.case.sentenceCaseUseCase')}</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-gray-800 mb-1">{t('converters.case.howToUse')}</h3>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>{t('converters.case.howToUseStep1')}</li>
                <li>{t('converters.case.howToUseStep2')}</li>
                <li>{t('converters.case.howToUseStep3')}</li>
                <li>{t('converters.case.howToUseStep4')}</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Home Link */}
      <div className="text-center">
        <LocalizedLink
          to="/"
          className="text-[#2563eb] hover:underline text-sm"
        >
          ← {t('common.backToAllConverters')}
        </LocalizedLink>
      </div>
    </div>
  );
};

export default CaseConverterIndex;
